import { isRouteErrorResponse, Links, Meta, Outlet, Scripts, ScrollRestoration } from 'react-router'
import './styles/theme.css'
import type { Route } from './+types/root'

/**
 * Server-side loader to provide runtime environment variables.
 * This runs on the server at request time, allowing us to read
 * environment variables from the managed runtime instance.
 */
// eslint-disable-next-line react-refresh/only-export-components
export async function loader() {
    // Read environment variables from server at runtime
    // These will be available from the managed runtime instance
    return {
        env: {
            OCAPI_CLIENT_ID: process.env.OCAPI_CLIENT_ID || process.env.VITE_OCAPI_CLIENT_ID || '',
            OCAPI_CLIENT_SECRET:
                process.env.OCAPI_CLIENT_SECRET || process.env.VITE_OCAPI_CLIENT_SECRET || '',
            SITE_ID: process.env.SITE_ID || process.env.VITE_SITE_ID || '',
            CATALOG_ID: process.env.CATALOG_ID || process.env.VITE_CATALOG_ID || '',
            STOREFRONT_URL: process.env.STOREFRONT_URL || process.env.VITE_STOREFRONT_URL || '',
            OAUTH_URL: process.env.OAUTH_URL || process.env.VITE_OAUTH_URL || ''
        }
    }
}

/**
 * Links function to include external stylesheets
 * Add any additional stylesheets or preload links here
 */
// eslint-disable-next-line react-refresh/only-export-components
export const links: Route.LinksFunction = () => [
    { rel: 'preconnect', href: 'https://fonts.googleapis.com' },
    { rel: 'preconnect', href: 'https://fonts.gstatic.com', crossOrigin: 'anonymous' },
    {
        rel: 'stylesheet',
        href: 'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600&family=Space+Grotesk:wght@500;700&display=swap'
    }
]

/**
 * Root layout component
 * This wraps all pages and provides the HTML structure
 */
export function Layout({ children }: { children: React.ReactNode }) {
    return (
        <html lang='en'>
            <head>
                <meta charSet='utf-8' />
                <meta name='viewport' content='width=device-width, initial-scale=1' />
                <title>Category Search Extension - Amplience</title>
                <Meta />
                <Links />
            </head>

            <body id='root' className='min-h-screen bg-slate-950'>
                {children}
                <ScrollRestoration />
                <Scripts />
            </body>
        </html>
    )
}

/**
 * Root app component
 * This is the main entry point for all routes
 */
export default function App() {
    return <Outlet />
}

/**
 * Error boundary component
 * Handles errors that occur during rendering
 */
export function ErrorBoundary({ error }: Route.ErrorBoundaryProps) {
    let message = 'Oops!'
    let details = 'An unexpected error occurred.'
    let stack: string | undefined

    if (isRouteErrorResponse(error)) {
        message = error.status === 404 ? '404' : 'Error'
        details =
            error.status === 404
                ? 'The requested page could not be found.'
                : error.statusText || details
    } else if (import.meta.env.DEV && error && error instanceof Error) {
        details = error.message
        stack = error.stack
    }

    return (
        <main className='container mx-auto flex min-h-screen flex-col items-center justify-center p-4'>
            <div className='max-w-2xl text-center'>
                <h1 className='mb-4 text-6xl font-bold text-slate-100'>{message}</h1>
                <p className='mb-8 text-xl text-slate-400'>{details}</p>
                {stack && (
                    <div className='rounded-lg bg-slate-900 p-6 text-left'>
                        <h2 className='mb-2 text-lg font-semibold text-slate-200'>Stack Trace:</h2>
                        <pre className='overflow-x-auto text-sm text-rose-400'>
                            <code>{stack}</code>
                        </pre>
                    </div>
                )}
            </div>
        </main>
    )
}
